import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { appCfgApi } from '../services/api';

const LookupContext = createContext(null);

/**
 * LookupProvider - Caches all lookup values on app load.
 * Provides getValues(category) for dropdown options.
 */
export const LookupProvider = ({ children }) => {
  const [lookups, setLookups] = useState({});   // { category: [values] }
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await appCfgApi.getAllLookups();
      setLookups(res.data);
    } catch (err) {
      // Status first: a 401/403 leaves every lookup dropdown empty and every
      // lookup-backed cell showing its raw index, so the cause needs to be obvious.
      const status = err?.response?.status;
      console.error(`Failed to load lookups${status ? ` (HTTP ${status})` : ''}:`, err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => { load(); }, [load]);

  // Refetch when the selected database changes. View-based lookups (e.g. Parent_Unique,
  // Positive_Terminal, Negative_Terminal) are scoped to the project DB — a cached copy
  // from the previous DB would render dropdowns with the wrong project's data.
  // DatabaseContext dispatches this event when the URL slug flips to a different DB.
  //
  // Also refetch on pmd-lookups-invalidated — dispatched by the api.js response
  // interceptor after any successful POST/PUT/DELETE via dynamicApi. Adding a new
  // Panels row should make it available in Tags dropdowns immediately, without a
  // page refresh. Backend clears its own lookups cache on the same events.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('pmd-database-changed', handler);
    window.addEventListener('pmd-lookups-invalidated', handler);
    return () => {
      window.removeEventListener('pmd-database-changed', handler);
      window.removeEventListener('pmd-lookups-invalidated', handler);
    };
  }, [load]);

  // Get values for a category
  const getValues = useCallback((category) => {
    return lookups[category] || [];
  }, [lookups]);

  // Force refresh (after admin edits)
  const refresh = useCallback(async () => { await load(); }, [load]);

  return (
    <LookupContext.Provider value={{ lookups, loading, getValues, refresh }}>
      {children}
    </LookupContext.Provider>
  );
};

export const useLookups = () => {
  const context = useContext(LookupContext);
  if (!context) {
    throw new Error('useLookups must be used within a LookupProvider');
  }
  return context;
};

export default LookupContext;
